import { SupportedProviders } from '@common/Fabric/Connections/types';
import { useOAuthLogin } from '@common/Fabric/DatabricksOAuth';
import { LoginProtocol } from '@common/Fabric/DatabricksOAuth/api';
import { OrchestrationBQAuthType } from '@common/Fabric/types/orchestration';
import { SentryTags } from '@common/sentry/SentryTags';
import {
  GenericProviderFormData,
  SqlBigQueryProvider,
  SqlProvider,
  UnifiedBigQueryFormData
} from '@pages/Fabric/types';
import { toast } from '@prophecy/ui-v3/LegacyToast';
import { ProphecyError } from '@prophecy/utils/error';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { SQL_PROVIDER } from 'frontend/core/src/constants/sql';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { handleXHRError, useRestMutation } from 'frontend/core/src/data/util';
import { UseFormReturn } from 'react-hook-form';

import { testSqlConnection } from '../../api/sqlAPI';
import { useSetTestConnection } from '../../common/TestConnectionContext';
import { getUnifiedBigQueryOAuthPayload, SERVICE_ACCOUNT_KEY_PLACEHOLDER } from './utils';

export const useUnifiedBigQueryTestConnection = (
  formContext: UseFormReturn<GenericProviderFormData>,
  supportedProviders?: SupportedProviders,
  fabricId?: string
) => {
  const globalConfigManager = useGlobalVariables();
  const executionServiceUrl = globalConfigManager.getExecutionURL as string;
  const { isUserLoggedIn } = useOAuthLogin();

  const testConnection = usePersistentCallback(
    async ({ showSuccessToast = false, skipOAuthLogin = false } = {}): Promise<boolean> => {
      try {
        const bqFormContext = formContext as unknown as UseFormReturn<UnifiedBigQueryFormData>;
        const fabricData = bqFormContext.getValues('unifiedFabricData')!;

        const { authType } = fabricData;
        const isPrivateKey = authType === OrchestrationBQAuthType.PrivateKey;
        const isOAuth = authType === OrchestrationBQAuthType.OAuth;

        // For OAuth U2M, check if user is logged in (triggers the login dialog if not)
        if (isOAuth) {
          const providerFormData = formContext.getValues() as GenericProviderFormData;

          const isLoggedIn = await isUserLoggedIn({
            protocol: LoginProtocol.OAuth,
            loginType: 'callback',
            payload: getUnifiedBigQueryOAuthPayload(providerFormData, supportedProviders),
            disableAutoTokenSync: !fabricId,
            skipLogin: skipOAuthLogin
          });

          if (!isLoggedIn) {
            return false;
          }
        }

        // Test SQL connection — BigQuery's SQL test-connection endpoint is private-key only,
        // mirrors the legacy BigQueryForm mapping (dataset/keyJson/kmsEncryptionKey)
        const providerInfo: SqlBigQueryProvider = {
          providerType: SQL_PROVIDER.BigQuery,
          dataset: fabricData.dataset,
          keyJson:
            isPrivateKey && fabricData.serviceAccountKey !== SERVICE_ACCOUNT_KEY_PLACEHOLDER
              ? fabricData.serviceAccountKey || ''
              : '',
          // TODO: Add support for OAuth key in testConnection
          kmsEncryptionKey: fabricData.kmsKey
        };

        await testSQLConnection({ providerInfo });

        if (showSuccessToast) {
          toast.success({
            content: 'Test connection successful',
            duration: 3000
          });
        }

        return true;
      } catch (e) {
        handleXHRError(e as ProphecyError);
        return false;
      }
    }
  );

  const { mutateAsync: testSQLConnection, isLoading } = useRestMutation(
    (params: SqlProvider) => testSqlConnection(params, executionServiceUrl, fabricId),
    {
      sentryTag: SentryTags.ProphecyErrorHttpType
    }
  );

  useSetTestConnection(testConnection, isLoading);
};
